import AppContext from "PFApp/app_context";
import { useGrowl } from "PFApp/use_growl";
import { useAccountCreate } from "PFCore/hooks/queries/accounts/use_account_create";
import { useAccountUpdate } from "PFCore/hooks/queries/accounts/use_account_update";
import { useLocalesUpdate } from "PFCore/hooks/queries/accounts/use_locales_update";
import { CURRENT_ACCOUNT_ASYNC_ACTIONS } from "PFReducers/current_account_reducer";
import { useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import { EditAccountForm, FormErrors } from "./edit_account.types";

type UseSaveAccountReturn = {
  saveAccount: (accountId: number | undefined, data: EditAccountForm) => void;
  isSaving: boolean;
  formErrors: FormErrors | null;
};

export const useSaveAccount = (accountId: number): UseSaveAccountReturn => {
  const { dispatch } = useContext(AppContext);
  const { t } = useTranslation("saasAdmin");
  const growl = useGrowl();
  const [formErrors, setFormErrors] = useState<FormErrors | null>(null);

  const onAccountSuccess = useCallback(() => {
    setFormErrors(null);
    growl({
      message: t("accounts.account.accountFormSaveSuccess"),
      kind: "success"
    });
  }, []);

  const onAccountError = useCallback((errors: FormErrors) => {
    if (errors?.owner instanceof Array) {
      errors.owner = errors.owner[0];
    }
    setFormErrors(errors);
    growl({
      message: t("accounts.account.accountFormSaveError"),
      kind: "error"
    });
  }, []);

  const onLocalesSuccess = useCallback(() => {
    dispatch({
      type: CURRENT_ACCOUNT_ASYNC_ACTIONS.CURRENT_ACCOUNT_FETCH
    });
    growl({
      message: t("accounts.account.languageSettingsSaveSuccess"),
      kind: "success"
    });
  }, []);

  const onLocalesError = useCallback(() => {
    growl({
      message: t("accounts.account.languageSettingsSaveError"),
      kind: "error"
    });
  }, []);

  const { mutate: createAccount, isLoading: isCreateAccountLoading } = useAccountCreate({
    onSuccess: onAccountSuccess,
    onError: onAccountError
  });
  const { mutate: updateAccount, isLoading: isUpdateAccountLoading } = useAccountUpdate({
    onSuccess: onAccountSuccess,
    onError: onAccountError
  });

  const { mutate: updateLocales, isLoading: isUpdateLocalesLoading } = useLocalesUpdate({
    accountId,
    onSuccess: onLocalesSuccess,
    onError: onLocalesError
  });

  const saveAccount = (accountId: number | undefined, data: EditAccountForm) => {
    if (accountId) {
      updateAccount({ accountId, accountData: data });
      updateLocales(data.languageSettings);
    } else {
      createAccount(data);
    }
  };
  return {
    saveAccount,
    isSaving: isCreateAccountLoading || isUpdateAccountLoading || isUpdateLocalesLoading,
    formErrors
  };
};
